import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Box,
  Button,
  CollapsableCard,
  Link,
  Text,
} from '@factorialco/deprecated-design-system'
import { EditLeavePageDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import LeaveApprovalRecommendation from 'modules/timeoff/components/LeaveApprovalRecommendation'
import Content from 'modules/timeoff/components/LeaveContent'
import AbsenceSummaryContent from 'modules/timeoff/components/LeaveContent/OtherLeavesForm/DayTypeForm/DayTypes/Days/AbsenceSummary'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { leaveEmployee } from 'modules/timeoff/lib/leaveEmployees'
import allowances from 'modules/timeoff/store/collections/allowances'
import leaveHistories from 'modules/timeoff/store/collections/leaveHistories'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'
import type LeaveHistory from 'modules/timeoff/store/models/LeaveHistory'

import ActionButtons from '../EditLeave/ActionButtons'
import { isAuthorSameAsOwner } from '../EditLeave/lib/isAuthorSameAsOwner'
import useCustomFields from '../EditLeave/useCustomFields'
import useForms from '../EditLeave/useForms'
import useLeaveData from '../EditLeave/useLeaveData'
import { useMaterializedApprovalFlowApproverCheck } from '../EditLeave/useMaterializedApprovalFlowApproverCheck'
import useModalCopies from '../EditLeave/useModalCopies'
import useUpdateLeave from '../EditLeave/useUpdateLeave'
import {
  RECOMMENDATION_ACTION,
  RecommendationAction,
} from '../LeaveApprovalRecommendation/RecommendationAction'
import type {
  RecommendationReason,
  ShiftInfo,
} from '../LeaveApprovalRecommendation/getRecommendationDescriptionRich'
import ShiftCoverageModal from '../ShiftCoverageModal'
import { approveAndRefreshLeave } from './approveAndRefreshLeave'

interface ShiftCoverageInfo {
  shiftsInfo: ShiftInfo[]
  leaveStart: string
  leaveEnd: string
  employeeRequestName: string
  onGoToShifts: () => void
}

interface Props {
  leaveId: number
  onClose: () => void
  onChange?: () => void
  leaveYear?: number
  onReject: () => void
  fromCalendar?: boolean
  contractVersions: ContractVersions
  loading: boolean
  fromShifts?: boolean
  visible?: boolean
}

const LeaveRecommendationModal = ({
  leaveId,
  onClose,
  onChange,
  leaveYear,
  onReject,
  fromCalendar = false,
  contractVersions,
  loading,
  fromShifts = false,
  visible = true,
}: Props) => {
  const [showShiftCoverage, setShowShiftCoverage] = useState(false)
  const [shiftCoverageInfo, setShiftCoverageInfo] = useState<ShiftCoverageInfo | null>(null)
  const shouldClosePortalRef = useRef(true)
  const [latestRecommendationReason, setLatestRecommendationReason] =
    useState<RecommendationReason | null>(null)
  const [hasNewDocuments, setHasNewDocuments] = useState(false)
  const [leaveHistory, setLeaveHistory] = React.useState<LeaveHistory | null>(null)
  const [authorName, setAuthorName] = React.useState<string | null>(null)
  const [authorId, setAuthorId] = React.useState<number | null>(null)
  const [authorType, setAuthorType] = React.useState<'Access' | 'User' | null>(null)
  const [collapsableOpen, setCollapsableOpen] = useState(false)
  const [loadingGoToShifts, setLoadingGoToShifts] = useState(false)

  const { fetchData } = useLeaves()
  const { closePortal } = usePortal()

  useAsyncData(() => {
    if (!fromCalendar) return Promise.resolve()

    return allowances.fetch()
  }, [fromCalendar])

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const year = useMemo(() => leaveYear || moment().year(), [leaveYear])
  const {
    leave,
    leaveConfirmationDetails,
    loading: leaveDataLoading,
    employee,
    permissions,
    medicalLeave,
  } = useLeaveData({ leaveId })

  const { data, loading: pageLoading } = useQuery(EditLeavePageDocument, {
    variables: { id: leaveId },
  })

  const electronicSickLeaveEmployee = data?.timeoff.leave?.employee
  const delegatedApprover = data?.timeoff.leave?.delegatedApproverAccess

  const { fetchStats } = useFetchStats()

  const { form, documentForm } = useForms({
    leave,
    effectiveOn: medicalLeave.medicalEffectiveOn,
  })

  const labourUnits = useLabourUnits({
    form,
  })

  const customFields = useCustomFields({
    leave,
    employee,
    contractVersions,
  })

  const handleRecommendationReason = (
    action: RecommendationAction,
    reason: RecommendationReason | '' | null
  ) => {
    if (action === RECOMMENDATION_ACTION.REVIEW) {
      setLatestRecommendationReason(reason === '' ? null : reason)
    }
  }

  const { onSubmit, updateLoading } = useUpdateLeave({
    year,
    form,
    documentForm,
    leave,
    employee,
    fetchStats,
    canUpdateLeave: permissions.update,
    getCustomFieldsValues: customFields.getValues,
    onChange,
    closePortal: () => {
      if (shouldClosePortalRef.current) {
        onClose()
      }
    },
  })

  const { title, description } = useModalCopies({
    form,
    leave,
    employee,
    canApprove: permissions.approve,
  })

  const onFetchStats = useCallback(() => {
    fetchStats(leave)
  }, [leave.id])

  const onDocumentDropped = () => {
    setHasNewDocuments(true)
  }

  const formLeaveId = form.get('leave_type_id').value

  const leaveType = leaveTypes.get(+formLeaveId)

  const leaveIdAllowance = employee ? leaveType?.getAllowanceForEmployee(employee) : null

  const {
    company: { subscription },
    access,
    canManageTimeoff,
  } = useCurrentSession()

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    'timeoff',
    access.id
  )

  const {
    loading: isCurrentApproverLoading,
    isCurrentApprover,
    approvalFlowId,
  } = useMaterializedApprovalFlowApproverCheck({
    resourceId: leave.id,
    resourceType: 'Timeoff::Leave',
  })

  const isLoading =
    leaveDataLoading ||
    updateLoading ||
    isCurrentApproverLoading ||
    loadingApprovalFlows ||
    pageLoading

  const timeoffSupervisor = employee
    ? canManageTimeoff ||
      employee.isTimeoffSupervisedByCurrent() ||
      employee.isSupervisedByCurrent()
    : false

  const labourUnitsHours = labourUnits.label === Allowance.ALLOWANCE_TYPES.hours
  const hoursText = convertHoursToTimeFormat(labourUnits.value, true)
  const daysText = i18n.tp('time_off.days_with_years', {
    count: labourUnits.value,
  })
  const timeTaken = labourUnitsHours ? hoursText : daysText

  useEffect(() => {
    const isreviewTimeOffRequest = permissions.approve

    if (isreviewTimeOffRequest) {
      track(trackingEvents.timeoff.reviewTimeOffRequestStart, {
        from_shifts: fromShifts,
      })
    }
  }, [permissions.approve, fromShifts])

  const isSubmitDisabled = () => {
    const isDetailsRequired = leaveType?.attributes.details_required
    const hasLeaveDescription: string = form.get('description').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    if (isDetailsRequired) {
      return isLoading || !((form.isDirty || hasNewDocuments) && hasLeaveDescription)
    }

    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount

    return isLoading || !(form.isDirty || hasNewDocuments) || absurdStartTime
  }

  useEffect(() => {
    const fetchLeaveHistory = async () => {
      try {
        const fetchLeaveHistories = await leaveHistories.fetch({
          data: { leave_id: leave.id },
        })
        const dataHistory = fetchLeaveHistories ? fetchLeaveHistories[0] : null
        setLeaveHistory(dataHistory)
        setAuthorName(dataHistory.author_name)
        setAuthorId(dataHistory.author_id)
        setAuthorType(dataHistory.author_type)
      } catch (error) {}
    }
    fetchLeaveHistory()
  }, [leave.id])

  // TODO: it's not enough to check the access id since author in leave history is a polymorphic relationship.
  // Need to be fixed after backend exposes the author type (not critical for now)
  const canSeeRequester =
    leaveHistory != null &&
    (canManageTimeoff ||
      !isAuthorSameAsOwner({
        authorID: authorId,
        authorType: authorType,
        employee: employee,
      }))

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (employee?.policy.can('approveLeaves') || approvalFlows.length > 0)

  const employeeFirstName = data?.timeoff.leave?.employee?.firstName
  const safeEmployeeName =
    employeeFirstName === ''
      ? i18n.t('time_off.approval_recommendation.loading_author')
      : employeeFirstName

  const reviewTitle = i18n.t('time_off.approval_recommendation.author_timeoff_request_title', {
    authorName: safeEmployeeName,
  })
  const rejectionReason = leave.attributes.reason

  const isReviewerMode = permissions.approve && leave.isPending()

  if (isReviewerMode && !safeEmployeeName) return null

  const calendarEventStartOn = form.startOn?.isValid() ? form.startOn.toDate() : undefined
  const calendarEventFinishOn = form.finishOn?.isValid()
    ? form.finishOn.toDate()
    : calendarEventStartOn
  const calendarEventFinishOnNatural = labourUnits?.finishOnNatural
    ? new Date(labourUnits.finishOnNatural)
    : calendarEventStartOn
  const calculateCalendarEventFinishOn = () => {
    if (!form.finishOn?.isValid() && calendarEventStartOn) {
      return calendarEventStartOn
    }
    return (labourUnits?.isNatural || labourUnits?.countSmRestDays) && labourUnits?.finishOnNatural
      ? calendarEventFinishOnNatural
      : calendarEventFinishOn
  }

  const hideOverlaps = leave.isPending() && isReviewerMode

  const isStartDateEqualFinishDate =
    form.startOn?.isValid() && form.finishOn?.isValid()
      ? form.startOn.isSame(form.finishOn, 'day')
      : false

  const calculatedFinishOn = calculateCalendarEventFinishOn()

  const isPending = leave.isPending()
  const canApprove = permissions.approve
  const ownerId = access.id
  const isSameAsOwner =
    authorType === 'Access'
      ? authorId === ownerId
      : isAuthorSameAsOwner({
          authorID: authorId,
          authorType: authorType,
          employee: employee,
        })

  const handleApprove = async () => {
    const shiftInfo = await detectShiftCoverage()
    if (shiftInfo && Array.isArray(shiftInfo.shiftsInfo) && shiftInfo.shiftsInfo.length > 0) {
      await approveAndRefreshLeave({
        action: isCurrentApprover ? 'approve' : 'approve_all',
        rpcMethod: isCurrentApprover ? 'approve' : 'approve_all',
        leave,
        form,
        canUpdate: permissions.update,
        onFetchStats,
        fetchData: (...args) => fetchData(...args),
        closePortal,
        onClose: () => {},
        fromShifts,
      })
      setShowShiftCoverage(true)
      setShiftCoverageInfo(shiftInfo)
      shouldClosePortalRef.current = false
      return
    }
    shouldClosePortalRef.current = true
    await onSubmit()
    if (employee) fetchData(leaveEmployee(leave))
    if (onChange) onChange()
    onClose()
  }

  const detectShiftCoverage = async () => {
    const shiftsInfo: ShiftInfo[] = latestRecommendationReason?.shiftsInfo ?? []
    const shiftsLength = shiftsInfo.length
    if (shiftsLength > 0) {
      const goToShifts = () => {
        const firstShift = shiftsInfo[0]
        if (!firstShift) return
        const startDate = new Date(firstShift.startAt)
        const yearShift = startDate.getFullYear()
        const month = startDate.getMonth() + 1
        const day = startDate.getDate()
        const path = navigation.shifts.customViewGroup.fullPath({
          viewType: 'weekly',
          groupType: 'employees',
          year: yearShift,
          month,
          day,
        })
        navigate(path)
      }
      return {
        shiftsInfo,
        leaveStart: leave.attributes.start_on || '',
        leaveEnd: leave.attributes.finish_on || '',
        employeeRequestName: safeEmployeeName || '',
        onGoToShifts: goToShifts,
      }
    }
    return null
  }

  const handleGoToShifts = async () => {
    setLoadingGoToShifts(true)
    setShowShiftCoverage(false)
    setLoadingGoToShifts(false)
    setTimeout(() => {
      shiftCoverageInfo?.onGoToShifts?.()
    }, 0)
    onClose()
  }

  const isReviewWithShifts =
    isReviewerMode &&
    latestRecommendationReason?.shiftsInfo &&
    latestRecommendationReason.shiftsInfo.length > 0

  if (!visible) return null

  const loadingInfo = loading || leaveDataLoading || (isReviewerMode && safeEmployeeName === null)

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      {showShiftCoverage && shiftCoverageInfo ? (
        <ShiftCoverageModal
          visible={showShiftCoverage}
          onClose={() => {
            setShowShiftCoverage(false)
            onClose()
          }}
          shiftsInfo={shiftCoverageInfo.shiftsInfo}
          leaveStart={shiftCoverageInfo.leaveStart}
          leaveEnd={shiftCoverageInfo.leaveEnd}
          employeeRequestName={shiftCoverageInfo.employeeRequestName}
          onGoToShifts={handleGoToShifts}
        />
      ) : (
        <Modal
          illustration={isReviewerMode ? 'time-off' : 'calendar'}
          title={isReviewerMode ? reviewTitle : title}
          description={description}
          onSubmit={isLoading ? undefined : handleApprove}
          onClose={onClose}
          footer={
            !medicalLeave.isMedical &&
            (!permissions.readOnly || isCurrentApprover) && (
              <ActionButtons
                form={form}
                leave={leave}
                onFetchStats={onFetchStats}
                onReject={onReject}
                onChange={(...args) => {
                  if (onChange) {
                    onChange(...args)
                  }
                }}
                canApprove={permissions.approve}
                canUpdate={permissions.update}
                canRemove={permissions.remove}
                isSubmitDisabled={isSubmitDisabled()}
                isManagerOrAdmin={timeoffSupervisor}
                isCurrentApprover={!!isCurrentApprover}
                approvalFlowId={approvalFlowId}
                isLoading={isLoading}
                fromShifts={fromShifts}
                onClose={onClose}
                onlySubmit={isReviewWithShifts ? true : undefined}
              />
            )
          }
        >
          {loadingInfo || loadingGoToShifts ? (
            <Loading expand />
          ) : (
            <>
              {calendarEventStartOn && calculatedFinishOn && (
                <Box paddingBottom="s6">
                  <AbsenceSummaryContent
                    title={leaveType?.name || ''}
                    description={timeTaken}
                    fromDate={isStartDateEqualFinishDate ? undefined : calendarEventStartOn}
                    toDate={calculatedFinishOn}
                    color="group1"
                  />
                </Box>
              )}
              <Box paddingY="s12">
                <CollapsableCard
                  open={collapsableOpen}
                  contentBackground="lighter000"
                  contentPadding="s0"
                  Card={
                    <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
                      <Text.Normal weight="medium">
                        {i18n.t('time_off.approval_recommendation.absence_details')}
                      </Text.Normal>{' '}
                      <Button
                        onClick={() => setCollapsableOpen((open) => !open)}
                        type="secondary"
                        iconName={collapsableOpen ? 'chevron-up' : 'chevron-down'}
                      />
                    </Box>
                  }
                  Content={
                    <>
                      <Box
                        border={{
                          top: {
                            width: 's1',
                            style: 'solid',
                            color: 'grey400',
                          },
                        }}
                      />
                      <Box padding="s12">
                        <Content
                          employee={employee}
                          leaveTypes={leaveTypes}
                          documentForm={documentForm}
                          form={form}
                          leave={leave}
                          leaveId={leaveId}
                          readOnly={permissions.readOnlyFields}
                          showEmployeeSelector={false}
                          year={year}
                          customFieldsForms={customFields.forms}
                          onDocumentDropped={onDocumentDropped}
                          leaveConfirmationDetails={leaveConfirmationDetails}
                          contractVersions={contractVersions}
                          timeTaken={timeTaken}
                          labourUnits={labourUnits}
                          electronicSickLeaveEmployee={electronicSickLeaveEmployee}
                          isReviewOrEditMode
                        />
                      </Box>
                    </>
                  }
                />
              </Box>
              <Box gap="s8">
                <Box gap="s8">
                  {leaveIdAllowance && (
                    <FranceCalendarWarnings
                      allowance={leaveIdAllowance}
                      admin={timeoffSupervisor}
                      labourUnits={labourUnits}
                    />
                  )}
                  {canDelegateApprover && employee && (
                    <DelegateApproverSelector
                      category="timeoff"
                      approver={delegatedApprover ?? null}
                      employee={employee}
                      field={form.get('delegated_approver_id')}
                      startsOn={form.get('start_on').value}
                      finishOn={form.get('finish_on').value}
                    />
                  )}
                  {medicalLeave.isMedical && (
                    <Alert color="warning">
                      <Text.Normal>
                        {i18n.t('time_off.medical_leave.not_editable_notice.description')}
                      </Text.Normal>
                      <Link.Href href={helpCenterUrl('new_request')} target="_blank">
                        {i18n.t('time_off.medical_leave.not_editable_notice.link')}
                      </Link.Href>
                    </Alert>
                  )}
                </Box>
                <ApprovalsInformationBlock
                  resourceId={leave.id}
                  resourceType="Timeoff::Leave"
                  defaultOpen={false}
                />
                {rejectionReason && (
                  <Text.Normal size="200" color="grey800" overflowWrap>
                    {i18n.t('time_off.rejection_reason')}: {rejectionReason}
                  </Text.Normal>
                )}
                {canSeeRequester && (
                  <Box paddingTop="s6">
                    <Text.Normal size="300" color="grey800">
                      {i18n.t('time_off.requester_label')}: {authorName}
                    </Text.Normal>
                  </Box>
                )}
              </Box>

              {isPending && canApprove && !isSameAsOwner ? (
                <LeaveApprovalRecommendation
                  leave={leave}
                  author={safeEmployeeName ? safeEmployeeName : ''}
                  onRecommendationData={handleRecommendationReason}
                />
              ) : (
                <>
                  {!hideOverlaps && (
                    <Box paddingY="s16">
                      <LeaveInfo form={form} />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Modal>
      )}
    </ExpiredTrialChecker>
  )
}

export default observer(LeaveRecommendationModal)
